import { createStorefrontApiClient } from '@shopify/storefront-api-client';

// Add debugging
const DEBUG = true;
const STORE_DOMAIN = 'james-brown-shop.myshopify.com';
const API_VERSION = '2025-04';

const client = createStorefrontApiClient({
	storeDomain: STORE_DOMAIN,
	publicAccessToken: '29e00005dee7b652d8f09962b9f7b550',
	apiVersion: API_VERSION
});

// Cart fragment for reuse across queries
const CART_FRAGMENT = `
	fragment CartFields on Cart {
		id
		checkoutUrl
		totalQuantity
		lines(first: 100) {
			edges {
				node {
					id
					quantity
					merchandise {
						... on ProductVariant {
							id
							title
							quantityAvailable
							price {
								amount
								currencyCode
							}
							product {
								title
								images(first: 1) {
									edges {
										node {
											url
										}
									}
								}
							}
						}
					}
				}
			}
		}
		cost {
			subtotalAmount {
				amount
				currencyCode
			}
			totalAmount {
				amount
				currencyCode
			}
			totalTaxAmount {
				amount
				currencyCode
			}
		}
		discountCodes {
			code
			applicable
		}
		discountAllocations {
			discountedAmount {
				amount
				currencyCode
			}
		}
	}
`;

// Test the connection with a simple query
async function testConnection() {
	try {
		const query = `{
			shop {
				name
			}
		}`;

		const response = await fetch(`https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': '29e00005dee7b652d8f09962b9f7b550'
			},
			body: JSON.stringify({ query })
		});

		const data = await response.json();
		if (DEBUG) console.log('Shop connection test:', data);
		return !data.errors;
	} catch (error) {
		console.error('Shop connection test failed:', error);
		return false;
	}
}

const store = {
	state: {
		cart: null,
		cartLoaded: false,
		showCart: false,
		updatingCart: false,
		cartLoading: false
	},
	async init() {
		if (DEBUG) console.log('Initializing store...');
		try {
			const isConnected = await testConnection();
			if (!isConnected) {
				throw new Error('Failed to connect to Shopify store');
			}
			if (DEBUG) console.log('Successfully connected to Shopify store');

			const existingCartId = localStorage.getItem("shopify_cart_id");
			if (existingCartId) {
				if (DEBUG) console.log('Found existing cart ID:', existingCartId);
				try {
					await this.fetchCart(existingCartId);
					
					// Verify we got a valid cart
					if (!this.state.cart?.id) {
						if (DEBUG) console.log('Failed to fetch existing cart, creating new one');
						localStorage.removeItem("shopify_cart_id");
						await this.createCart();
					}
				} catch (error) {
					console.error('Error fetching existing cart:', error);
					localStorage.removeItem("shopify_cart_id");
					await this.createCart();
				}
			} else {
				if (DEBUG) console.log('No existing cart, creating new one');
				await this.createCart();
			}

			// Final validation
			if (!this.state.cart?.id) {
				throw new Error('Failed to initialize cart');
			}
			
			if (DEBUG) console.log('Cart initialization complete:', this.state.cart);
		} catch (error) {
			console.error('Error initializing cart:', error);
			this.state.cart = null;
			this.state.cartLoaded = false;
			localStorage.removeItem("shopify_cart_id");
			this.noty('error', 'Unable to initialize cart. Please refresh the page.');
		}
	},
	async makeStorefrontRequest(query, variables = null) {
		try {
			const body = variables 
				? JSON.stringify({ query, variables })
				: JSON.stringify({ query });

			const response = await fetch(`https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Shopify-Storefront-Access-Token': '29e00005dee7b652d8f09962b9f7b550'
				},
				body
			});

			const data = await response.json();
			if (data.errors) {
				throw new Error(data.errors[0].message);
			}
			return data;
		} catch (error) {
			console.error('Storefront request failed:', error);
			throw error;
		}
	},
	async fetchCart(cartId) {
		try {
			if (DEBUG) console.log('Fetching cart with ID:', cartId);
			
			const query = `
				query GetCart($cartId: ID!) {
					cart(id: $cartId) {
						...CartFields
					}
				}
				${CART_FRAGMENT}
			`;

			const response = await this.makeStorefrontRequest(query, { cartId });

			if (DEBUG) console.log('Fetch cart response:', response);

			if (response?.data?.cart) {
				this.state.cart = response.data.cart;
				this.state.cartLoaded = true;
			} else {
				if (DEBUG) console.log('Cart not found, creating new one');
				await this.createCart();
			}
		} catch (error) {
			console.error('Error fetching cart:', error);
			if (DEBUG) console.log('Full error:', error);
			await this.createCart();
		}
	},
	async createCart() {
		try {
			const query = `
				mutation CartCreate {
					cartCreate {
						cart {
							...CartFields
						}
						userErrors {
							field
							message
						}
					}
				}
				${CART_FRAGMENT}
			`;

			const response = await this.makeStorefrontRequest(query);

			if (response?.data?.cartCreate?.cart) {
				this.state.cart = response.data.cartCreate.cart;
				this.state.cartLoaded = true;
				localStorage.setItem("shopify_cart_id", response.data.cartCreate.cart.id);
			} else if (response?.data?.cartCreate?.userErrors?.length > 0) {
				console.error('Cart creation errors:', response.data.cartCreate.userErrors);
				this.noty('error', response.data.cartCreate.userErrors[0].message);
			}
		} catch (error) {
			console.error('Error creating cart:', error);
			this.noty('error', 'Unable to create cart');
		}
	},
	async addLineItem(variantId, quantity) {
		if (DEBUG) console.log('Adding line item:', { variantId, quantity, currentCart: this.state.cart });
		
		try {
			// If no cart exists or cart is invalid, create a new one
			if (!this.state.cart?.id) {
				if (DEBUG) console.log('No valid cart found, creating new cart');
				await this.createCart();
				
				// Double check we have a valid cart after creation
				if (!this.state.cart?.id) {
					throw new Error('Failed to create cart');
				}
			}
			
			this.state.updatingCart = true;
			
			const query = `
				mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
					cartLinesAdd(cartId: $cartId, lines: $lines) {
						cart {
							...CartFields
						}
						userErrors {
							field
							message
							code
						}
					}
				}
				${CART_FRAGMENT}
			`;

			const variables = {
				cartId: this.state.cart.id,
				lines: [{
					merchandiseId: variantId,
					quantity: parseInt(quantity, 10)
				}]
			};

			if (DEBUG) console.log('Sending cart add request:', variables);

			const response = await this.makeStorefrontRequest(query, variables);
			if (DEBUG) console.log('Cart add response:', response);

			if (response?.data?.cartLinesAdd?.cart) {
				this.state.cart = response.data.cartLinesAdd.cart;
				this.state.cartLoaded = true;
				this.state.showCart = true;
				
				localStorage.setItem("shopify_cart_id", this.state.cart.id);
				// this.noty('success', 'Added to cart successfully');
			} else if (response?.data?.cartLinesAdd?.userErrors?.length > 0) {
				const error = response.data.cartLinesAdd.userErrors[0];
				
				if (error.code === 'INVENTORY_NOT_AVAILABLE') {
					this.noty('error', 'Not enough items available in stock');
				} else {
					this.noty('error', error.message);
				}
			}
		} catch (error) {
			console.error('Error adding to cart:', error);
			this.noty('error', 'Unable to add item to cart. Please try again.');
			
			// If we hit an error, clear the cart and try to create a new one
			this.state.cart = null;
			localStorage.removeItem("shopify_cart_id");
			await this.createCart();
		} finally {
			this.state.updatingCart = false;
		}
	},
	async updateLineItem(lineId, quantity) {
		if (!this.state.cart?.id) return;
		
		this.state.updatingCart = true;
		try {
			const query = `
				mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
					cartLinesUpdate(cartId: $cartId, lines: $lines) {
						cart {
							...CartFields
						}
						userErrors {
							field
							message
						}
					}
				}
				${CART_FRAGMENT}
			`;

			const variables = {
				cartId: this.state.cart.id,
				lines: [{
					id: lineId,
					quantity: parseInt(quantity, 10)
				}]
			};

			const response = await this.makeStorefrontRequest(query, variables);

			if (response?.data?.cartLinesUpdate?.cart) {
				this.state.cart = response.data.cartLinesUpdate.cart;
			} else if (response?.data?.cartLinesUpdate?.userErrors?.length > 0) {
				this.noty('error', response.data.cartLinesUpdate.userErrors[0].message);
			}
		} catch (error) {
			console.error('Error updating cart:', error);
			this.noty('error', 'Unable to update cart');
		} finally {
			this.state.updatingCart = false;
		}
	},
	async removeLineItem(lineId) {
		if (!this.state.cart?.id) return;
		
		this.state.updatingCart = true;
		try {
			const query = `
				mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
					cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
						cart {
							...CartFields
						}
						userErrors {
							field
							message
						}
					}
				}
				${CART_FRAGMENT}
			`;

			const variables = {
				cartId: this.state.cart.id,
				lineIds: [lineId]
			};

			const response = await this.makeStorefrontRequest(query, variables);

			if (response?.data?.cartLinesRemove?.cart) {
				this.state.cart = response.data.cartLinesRemove.cart;
			} else if (response?.data?.cartLinesRemove?.userErrors?.length > 0) {
				this.noty('error', response.data.cartLinesRemove.userErrors[0].message);
			}
		} catch (error) {
			console.error('Error removing from cart:', error);
			this.noty('error', 'Unable to remove item from cart');
		} finally {
			this.state.updatingCart = false;
		}
	},
	noty(type, message) {
		new Noty({
			type: type,
			text: message
		}).show();
	},
	helpers: {
		moneyFormat: function (input) {
			return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(input);
		}
	}
};

export { store, client };
